import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ArticleCard } from './article-card';
import { useSavedQuery } from '../hooks/use-feed-query';
import type { FeedArticle } from '../types';

interface FeedGridProps {
  readonly articles: readonly FeedArticle[];
  readonly isLoading: boolean;
  readonly isError?: boolean;
  readonly emptyMessage?: string;
  readonly hasNextPage?: boolean;
  readonly fetchNextPage?: () => void;
  readonly isFetchingNextPage?: boolean;
}

export function FeedGrid({
  articles,
  isLoading,
  isError = false,
  emptyMessage,
  hasNextPage,
  fetchNextPage,
  isFetchingNextPage,
}: FeedGridProps) {
  const { t } = useTranslation();
  const saved = useSavedQuery();
  const savedSet = useMemo(() => new Set(saved.data ?? []), [saved.data]);

  if (isLoading) {
    return (
      <div className="nw-feed__loading">
        <div className="nw-feed__loading-spinner" aria-hidden />
        {t('web.feedGrid.loading')}
      </div>
    );
  }
  if (isError) {
    return (
      <div className="nw-feed__empty">
        {t('web.feedGrid.errorNetwork')}
      </div>
    );
  }
  if (articles.length === 0) {
    return <div className="nw-feed__empty">{emptyMessage ?? t('web.feedGrid.emptyDefault')}</div>;
  }
  return (
    <>
      <div className="nw-feed__grid">
        {articles.map((a) => (
          <ArticleCard key={a.id} article={a} isSaved={savedSet.has(a.id)} />
        ))}
      </div>
      {hasNextPage && fetchNextPage && (
        <div style={{ textAlign: 'center', marginTop: 8 }}>
          <button
            type="button"
            className="nw-btn nw-btn--ghost"
            onClick={fetchNextPage}
            disabled={isFetchingNextPage}
          >
            {isFetchingNextPage ? t('web.feedGrid.loadingMore') : t('web.feedGrid.loadMore')}
          </button>
        </div>
      )}
    </>
  );
}
